import React, { useContext, useEffect, useState, useRef } from "react";
import dayjs from "dayjs";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Slide from "@mui/material/Slide";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

import UserAvatar from "../../management/UserAvatar";
import firebase from "../../../firebase";
import ConversationMessage from "../ConversationMessage";
import ConfirmDeleteDialog from "../../ConfirmDeleteDialog";
import { UserContext } from "../../../context";
import ConversationGroupAvatar from "../ConversationGroupAvatar";
import { markConversationRead } from "../messageReadReceipts";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const AlwaysScrollToBottom = () => {
    const elementRef = useRef();

    useEffect(() => {
        elementRef.current?.scrollIntoView();
    });

    return <div ref={elementRef} />;
};

export default function ConversationDetailMobile(props) {
    const [text, setText] = useState("");
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
        useState(false);
    const { currentUserData } = useContext(UserContext);
    const currentUserKey = currentUserData?.key;
    const [conversation, setConversation] = useState();
    const pendingReadReceipts = useRef(new Set());

    useEffect(() => {
        if (props.conversation) {
            if (props.conversation.deleted === false) {
                setConversation(props.conversation);
            }

            markConversationRead(
                props.conversation,
                currentUserKey,
                pendingReadReceipts.current,
            ).catch((error) => {
                console.error("Unable to mark conversation as read", error);
            });
        }
    }, [props.conversation, currentUserKey]);

    function handleTextInput(event) {
        setText(event.target.value);
    }

    function handleReplyClick() {
        if (conversation) {
            let messageRef = firebase
                .database()
                .ref("conversations/" + conversation.key + "/messages")
                .push();

            let message = {
                id: messageRef.key,
                message: text,
                date: dayjs().toString(),
                user: conversation.user.key,
                fullName: conversation.user.fullName,
                deleted: false,
            };

            messageRef.set(message);

            message.readBy = [];
            message.key = message.id;

            let conversationRef = firebase
                .database()
                .ref("conversations")
                .child(conversation.key);
            conversationRef.update({ lastMessage: dayjs().toString() });

            setText("");

            conversation.messages.push(message);
            setConversation(conversation);
        }
    }

    function handleCloseOnDeleteDialog(result) {
        if (result === true && currentUserData && conversation) {
            let conversationRef = firebase
                .database()
                .ref("conversations")
                .child(conversation.key);

            conversationRef.update({ deleted: true });

            conversation.deleted = true;
            setConversation(undefined);
        }

        setConfirmDeleteDialogOpen(false);
    }

    return (
        <Dialog
            fullScreen
            open={props.open}
            TransitionComponent={Transition}
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: "background.subtle",
                        backgroundImage: (theme) =>
                            theme.palette.mode === "dark"
                                ? "radial-gradient(circle at 15% 10%, rgba(31, 184, 94, 0.07), transparent 28%)"
                                : "radial-gradient(circle at 15% 10%, rgba(0, 149, 61, 0.045), transparent 28%)",
                    },
                },
            }}
        >
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Box
                    sx={{
                        minHeight: 68,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        px: 1.25,
                        py: 1,
                        flexShrink: 0,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                                ? "rgba(25, 32, 28, 0.96)"
                                : "rgba(255, 255, 255, 0.94)",
                        boxShadow: "0 3px 14px rgba(20, 35, 25, 0.055)",
                    }}
                >
                    <IconButton
                        onClick={props.onClose}
                        aria-label="Back to conversations"
                        sx={{ flexShrink: 0 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    {conversation && (
                        <Box sx={{ display: "flex", flexShrink: 0 }}>
                            {conversation.otherUsers.length === 1 ? (
                                <UserAvatar
                                    userOrHub={conversation.otherUsers[0]}
                                    path="users"
                                    variant="circular"
                                />
                            ) : (
                                <ConversationGroupAvatar
                                    conversation={conversation}
                                />
                            )}
                        </Box>
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 800 }}>
                            {conversation && conversation.title}
                        </Typography>
                        {conversation && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ display: "block", mt: -0.25 }}
                            >
                                {conversation.otherUsers.length === 1
                                    ? "Direct conversation"
                                    : `${conversation.otherUsers.length + 1} participants`}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {conversation && (
                    <>
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: "auto",
                                scrollbarWidth: "thin",
                            }}
                        >
                            <List disablePadding sx={{ px: 1.5, py: 2 }}>
                                {conversation.messages
                                    .filter(
                                        (message) => message.deleted === false,
                                    )
                                    .sort((a, b) =>
                                        dayjs(a.date) > dayjs(b.date)
                                            ? 1
                                            : dayjs(b.date) > dayjs(a.date)
                                              ? -1
                                              : 0,
                                    )
                                    .map((message) => (
                                        <ConversationMessage
                                            conversation={conversation}
                                            message={message}
                                            key={message.key}
                                            hideMenu
                                        />
                                    ))}
                                <AlwaysScrollToBottom />
                            </List>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-end",
                                gap: 1,
                                px: 1.25,
                                py: 1.25,
                                pb: "max(10px, env(safe-area-inset-bottom))",
                                flexShrink: 0,
                                borderTop: "1px solid",
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                            }}
                        >
                            <TextField
                                id="mobile-message-input"
                                placeholder="Write a message…"
                                multiline
                                maxRows={4}
                                value={text}
                                fullWidth
                                onChange={handleTextInput}
                                size="small"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2.5,
                                        backgroundColor: "background.subtle",
                                    },
                                }}
                            />
                            <IconButton
                                aria-label="Send message"
                                onClick={handleReplyClick}
                                disabled={text === ""}
                                sx={{
                                    width: 42,
                                    height: 42,
                                    mb: 0.1,
                                    flexShrink: 0,
                                    color: "common.white",
                                    backgroundColor: "primary.main",
                                    "&:hover": {
                                        backgroundColor: "primary.dark",
                                    },
                                    "&.Mui-disabled": {
                                        color: "action.disabled",
                                        backgroundColor:
                                            "action.disabledBackground",
                                    },
                                }}
                            >
                                <SendRoundedIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </>
                )}

                {confirmDeleteDialogOpen && (
                    <ConfirmDeleteDialog
                        title="Delete conversation"
                        message="Are you sure you want to delete this conversation?"
                        open={confirmDeleteDialogOpen}
                        onClose={handleCloseOnDeleteDialog}
                    />
                )}
            </Box>
        </Dialog>
    );
}
